import { ControlledTextField } from 'controlled-components';
import { useValidatePhoneNumberMutation } from 'drycut-graphql';
import { DebouncedTextField, FormHelpText, Stack } from 'ui-components';
import { Controller, useFormContext } from 'react-hook-form';
import { useCheckoutStore } from './BookingCheckout/checkoutStore';

type Props = {
  onSuccessfulValidation?: () => void;
} & Omit<React.ComponentProps<typeof ControlledTextField>, 'name' | 'label' | 'value'>;

export function ControlledValidationPhoneField({
  onSuccessfulValidation,
  ...props
}: Props): React.ReactElement {
  const { trigger, setValue, setError, clearErrors } = useFormContext();
  const manual = useCheckoutStore((state) => state.manual);
  const [validatePhone, { loading }] = useValidatePhoneNumberMutation();
  const phoneRegex =
    /^(\+{0,})(\d{0,})([(]{1}\d{1,3}[)]{0,}){0,}(\s?\d+|\+\d{2,3}\s{1}\d+|\d+){1}[\s|-]?\d+([\s|-]?\d+){1,2}(\s){0,}$/gm;

  const defaultErrorMessage = 'Diese Telefonnummer ist ungültig';

  // Triggered when the user stops typing for 500ms
  const handleDebouncedChange = (value?: string) => {
    setValue('phone', value);
    trigger('phone', { shouldFocus: true });

    if (value && value.length >= 10) {
      validateNumber(value);
    }
  };

  const validateNumber = async (phoneNumber: string) => {
    const { data } = await validatePhone({ variables: { input: { phoneNumber } } });

    const validation = data?.validatePhoneNumber;

    if (!validation?.valid) {
      setError('phone', { type: 'manual', message: defaultErrorMessage });
    } else {
      clearErrors('phone');
      onSuccessfulValidation?.();
    }
  };

  return (
    <Controller
      name="phone"
      rules={{
        required: defaultErrorMessage,
        minLength: { value: 10, message: defaultErrorMessage },
        pattern: { value: phoneRegex, message: defaultErrorMessage },
      }}
      render={({
        field: { name, ref, value, onBlur, disabled },
        fieldState: { invalid, error },
      }) => (
        <Stack direction="col" gap={1} className="relative">
          <DebouncedTextField
            type="tel"
            align="left"
            autoComplete={manual ? 'off' : 'tel'}
            label=""
            placeholder="Telefonnummer"
            loading={loading}
            onDebouncedChange={(value) => handleDebouncedChange(value)}
            invalid={invalid}
            name={name}
            ref={ref}
            value={value}
            onBlur={onBlur}
            disabled={disabled}
            {...props}
          />
          {error?.message && <FormHelpText error={invalid}>{error?.message}</FormHelpText>}
        </Stack>
      )}
    />
  );
}
